import {
  AddAppAccountRequestPayload,
  TermsConsent,
} from "@/data/payload/app-account-setting/add-app-account-request-payload";
import { CheckUserIdAvailableRequestPayload } from "@/data/payload/app-account-setting/check-user-id-available-reqeuest-payload";
import { POLICY_CONSENT_URL, URLList } from "@/data/url_list";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATErrorModel, SATErrorType, SATResult } from "@sadtx/sat-error";
import SecureStorageService from "@sadtx/secure-storage";

export class AppAccountSettingSource {
  private readonly AUTH_TOKEN_STORAGE_KEY = "app-account-setting-token";
  private readonly TERMS_CONSENT_STORAGE_KEY = "app-account-setting-terms-consent";
  private readonly EXPIRED_AUTH_CODE_TOKEN_ERROR = new SATError(
    SATErrorType.ExpiredAuthCodeToken,
    "Expired auth code token"
  );

  //MARK:- Constructor
  constructor(
    private readonly client: FetchApiClientMethod,
    private readonly secureStorage: SecureStorageService
  ) {}

  async setAuthorizedToken(token: string): Promise<SATResult<void, SATError>> {
    try {
      await this.secureStorage.setItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY, token);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error("[AppAccountSettingSource] :: setAuthorizedToken :: 토큰 저장 실패", error);
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }

  async saveTermsConsent(termsConsent: TermsConsent[]): Promise<SATResult<void, SATError>> {
    try {
      await this.secureStorage.setItem(
        sessionStorage,
        this.TERMS_CONSENT_STORAGE_KEY,
        JSON.stringify(termsConsent)
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: saveTermsConsent :: 약관 동의 정보 저장 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      return SATResult.failure(satError);
    }
  }

  async isExistAuthorizedToken(): Promise<boolean> {
    try {
      await this.getAuthorizedToken();
      return true;
    } catch (error: unknown) {
      return false;
    }
  }

  async removeAuthorizedToken(): Promise<void> {
    await this.secureStorage.removeItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY);
  }

  /**
   * 사용자 계정 설정을 위한 SMS를 통해 접속하는 경우 사용자 이름 검증 요청
   * @param payload - 사용자 이름 검증 요청 데이터, 토큰, 이름
   * @returns {Promise<SATResult<void, SATError>>} 사용자 이름 검증 결과
   */
  async checkUserNameBySmsTokenForLoginAccount(
    name: string
  ): Promise<SATResult<void, SATError>> {
    try {
      const token = await this.getAuthorizedToken();
      const url = `${URLList.AUTH.CHECK_USER_NAME_BY_SMS_TOKEN}/${token}/${name}`;
      const response = await this.client.get(url);
      console.log(
        "[AppAccountSettingSource] :: checkUserNameBySmsToken :: 사용자 이름 검증 요청 성공",
        response.data
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: checkUserNameBySmsToken :: 사용자 이름 검증 요청 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: checkUserNameBySmsToken | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * SMS 인증을 통해 앱 계정(ID, 비밀번호 및 이용약관 동의 정보) 등록 요청
   * 쿠키 기반 사용자 인증
   * @param payload - 앱 계정 등록 및 약관 동의 요청 데이터
   * @returns {Promise<SATResult<void, SATError>>} 설정 결과
   */
  async addAppAccount(
    payload: AddAppAccountRequestPayload
  ): Promise<SATResult<void, SATError>> {
    try {
      const termsConsent = await this.getTermsConsent();
      const newPayload = {
        ...payload,
        termsConsent: termsConsent,
      };

      const url = `${URLList.AUTH.SET_ID_AND_PASSWORD_BY_SMS}`;
      console.log("[AppAccountSettingSource] :: addAppAccount :: 요청 URL", url);
      console.log(
        "[AppAccountSettingSource] :: addAppAccount :: 요청 Payload",
        JSON.stringify(newPayload, null, 2)
      );
      console.log("[AppAccountSettingSource] :: addAppAccount :: termsConsent 검증", {
        length: termsConsent.length,
        items: termsConsent.map((item) => ({
          termsId: item.termsId,
          version: item.version,
          isConsented: item.isConsented,
          consentedAt: item.consentedAt,
        })),
      });

      const response = await this.client.put(url, newPayload);
      console.log(
        "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 성공",
        response.data
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      // Axios 에러인 경우 상세 정보 로깅
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: unknown; status?: number; statusText?: string };
        };
        console.error(
          "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 실패",
          {
            status: axiosError.response?.status,
            statusText: axiosError.response?.statusText,
            responseData: axiosError.response?.data,
            error: error,
          }
        );
      } else {
        console.error(
          "[AppAccountSettingSource] :: addAppAccount :: ID 및 비밀번호 설정 요청 실패",
          error
        );
      }

      // getTermsConsent에서 throw된 에러인 경우 그대로 반환
      if (error instanceof SATError) {
        return SATResult.failure(error);
      }
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: addAppAccount :: 변환된 에러", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 사용자 아이디 사용 가능 여부 검증 요청
   * @param payload - 사용자 아이디 사용 가능 여부 검증 요청 데이터
   * @returns {Promise<SATResult<void, SATError>>} 사용자 아이디 사용 가능 여부 검증 결과
   */
  async checkUserIdAvailable(
    payload: CheckUserIdAvailableRequestPayload
  ): Promise<SATResult<void, SATError>> {
    try {
      const url = `${URLList.AUTH.CHECK_USER_ID_AVAILABLE}/${payload.loginId}`;
      const response = await this.client.get(url);
      console.log(
        "[AppAccountSettingSource] :: checkUserIdAvailable :: 사용자 아이디 가용성 검증 요청 성공",
        response.data
      );
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: checkUserIdAvailable :: 사용자 아이디 가용성 검증 요청 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: checkUserIdAvailable | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   *
   * @returns {Promise<SATResult<string, SATError>>} 개인정보 처리방침 파일 조회 결과
   */
  async fetchPrivayMdFile(locale: string): Promise<SATResult<ParentalConsentDto, SATError>> {
    try {
      // 브라우저 캐시를 무시하기 위해 타임스탬프 쿼리 파라미터 추가
      const url = `${POLICY_CONSENT_URL.Privacy(locale)}?_t=${Date.now()}`;
      const result = await this.client.get(url);

      const version = result.headers["x-ms-meta-version"];
      console.log(
        "[AppAccountSettingSource] :: fetchPrivayMdFile :: 개인정보 처리방침 파일 조회 성공",
        {
          version,
          contentLength: result.data?.length,
        }
      );
      return SATResult.success({ version, content: result.data });
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: fetchPrivayMdFile :: 개인정보 처리방침 파일 조회 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: fetchPrivayMdFil˝ | ", satError);
      return SATResult.failure(satError);
    }
  }

  async fetchTermsOfServiceFile(
    locale: string
  ): Promise<SATResult<ParentalConsentDto, SATError>> {
    try {
      // 브라우저 캐시를 무시하기 위해 타임스탬프 쿼리 파라미터 추가
      const url = `${POLICY_CONSENT_URL.TermsOfService(locale)}?_t=${Date.now()}`;
      const result = await this.client.get(url);

      const version = result.headers["x-ms-meta-version"];
      console.log(
        "[AppAccountSettingSource] :: fetchTermsOfServiceFile :: 서비스 이용약관 파일 조회 성공",
        {
          version,
          contentLength: result.data?.length,
        }
      );
      return SATResult.success({ version, content: result.data });
    } catch (error: unknown) {
      console.error(
        "[AppAccountSettingSource] :: fetchTermsOfServiceFile :: 서비스 이용약관 파일 조회 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AppAccountSettingSource] :: fetchTermsOfServiceFile | ", satError);
      return SATResult.failure(satError);
    }
  }

  //MARK: - Private
  private async getAuthorizedToken(): Promise<string> {
    try {
      const token = await this.secureStorage.getItem(sessionStorage, this.AUTH_TOKEN_STORAGE_KEY);
      if (!token) {
        throw this.EXPIRED_AUTH_CODE_TOKEN_ERROR;
      }
      return token;
    } catch (error: unknown) {
      throw this.EXPIRED_AUTH_CODE_TOKEN_ERROR;
    }
  }

  private async getTermsConsent(): Promise<TermsConsent[]> {
    try {
      const termsConsent = await this.secureStorage.getItem(
        sessionStorage,
        this.TERMS_CONSENT_STORAGE_KEY
      );

      // 타입 가드: null, undefined, 빈 문자열 체크
      if (!termsConsent || typeof termsConsent !== "string") {
        throw new Error("약관 동의 정보가 없거나, 타입이 올바르지 않습니다.");
      }

      // 문자열 "undefined" 체크 (혹시 모를 경우 대비)
      if (termsConsent === "undefined" || termsConsent.trim() === "") {
        throw new Error("약관 동의 정보가 없거나, 타입이 올바르지 않습니다.");
      }

      // JSON 파싱 시도
      const parsed = JSON.parse(termsConsent) as TermsConsent[];
      return parsed;
    } catch (error: unknown) {
      // 이미 SATError인 경우 그대로 throw
      if (error instanceof SATError) {
        throw error;
      }
      console.error(
        "[AppAccountSettingSource] :: getTermsConsent :: 약관 동의 정보 조회 실패",
        error
      );
      throw new Error("약관 동의 정보 조회 실패");
    }
  }
}
